import React, {
  useState, useEffect, useContext, useRef,
} from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { StoryblokComponent } from 'storyblok-js-client';
import { DateService } from '@rocheglobal/component-library/src/services/date/index';
import { blokToComponent } from '../helpers';
import { Props } from '../types';
import { PageStateContext } from '../../services';
import { RocheContactCardPerson } from './contact-card-person';

// eslint-disable-next-line max-len
const filterAuthors = (authors = []) => authors.filter(({ content }) => content.firstname || content.lastname);

// eslint-disable-next-line default-param-last
const getAuthorsMarkup = (authors = [], url: string, translations): string => authors
  .map(({
    content: { firstname = '', lastname = '' },
    uuid,
  }) => `<roche-text-link text="${firstname} ${lastname}" href="/${url}?authors=${uuid}"></roche-text-link>`)
  .join(` ${translations.and || 'und'} `);

// eslint-disable-next-line default-param-last
const getCategoriesMarkup = (categories = [], url: string): string => categories
  .map(({ name, uuid }) => `<roche-category url="/${url}?categories=${uuid}" text="${name}"></roche-category>`)
  .join(' ');

const getDateMarkup = (date: string | Date): string => {
  if (!date) return '';
  return DateService.formatDate(
    date,
    { day: '2-digit', year: 'numeric', month: '2-digit' },
  );
};

export const DidArticlePage = (
  { blok, getComponent }: Props<StoryblokComponent<string>>,
): JSX.Element => {
  const { storyblok } = useStaticQuery(graphql`
    {
      storyblok {
        settings: SettingsItems(by_slugs: "global-data/settings", resolve_links: "url") {
          items {
            content {
              did_article_subtitle
              did_author_contact_card_title
              did_related_articles_title
              did_newsletter_registration
              did_overview_page_link {
                story {
                  slug
                  path
                  fullSlug
                }
              }
            }
          }
        }
      }
    }
  `);

  const didSettings = storyblok?.settings?.items[0]?.content;
  const { translations } = useContext(PageStateContext);
  const [subtitle, setSubtitle] = useState<string>(didSettings?.did_article_subtitle || '');
  const filteredAuthors = useRef(filterAuthors(blok.authors));

  const fillSubtitleWithData = (text: string): string | null => {
    if (typeof text !== 'string') return null;
    const overviewPageUrl = (didSettings?.did_overview_page_link?.story?.fullSlug || '').replace(/\/$/, '');
    // eslint-disable-next-line no-param-reassign
    text = text.replace(/<[\s\S]*?>/ig, '');
    // eslint-disable-next-line no-param-reassign
    text = text.replace('{DATE}', getDateMarkup(blok.release_date));
    // eslint-disable-next-line no-param-reassign
    text = text.replace('{AUTHORS}', getAuthorsMarkup(filteredAuthors.current, overviewPageUrl, translations));
    // eslint-disable-next-line no-param-reassign
    text = text.replace('{CATEGORIES}', getCategoriesMarkup(blok.categories, overviewPageUrl));
    return text;
  };

  useEffect(() => {
    filteredAuthors.current = blok.authors;
  }, [blok.authors]);

  useEffect(() => {
    setSubtitle(fillSubtitleWithData(didSettings?.did_article_subtitle));
  }, [
    blok.authors,
    blok.categories,
    blok.release_date,
    didSettings?.did_article_subtitle,
    translations.and,
  ]);

  return (
    <>
      {/* stage */}
      {blok.stage && blokToComponent({
        blok: {
          // eslint-disable-next-line no-underscore-dangle
          _uid: `${blok._uid}-grid-1`,
          component: 'roche-grid-12',
          slotted: blok.stage,
        },
        getComponent,
      }, 'stage')}

      <roche-grid layout="8" key='intro'>
        {subtitle && <roche-richtext style-option="intro-small">
          <p><roche-icon style={{
            position: 'relative',
            top: '3px',
            marginRight: '5px',
          }} icon="date"/> <span dangerouslySetInnerHTML={{ __html: subtitle }}/></p>
        </roche-richtext>}

        {blok.intro && blok.intro[0]
        && <>
          <roche-divider/>
          {blokToComponent({ blok: { ...blok.intro[0], style: 'intro' }, getComponent }, 'intro')}
        </>}
      </roche-grid>

      {/* content block */}
      {blok.content_block?.map(
        (item) => blokToComponent({ blok: item, getComponent }, 'content_block'),
      )}

      <roche-divider/>

      {/* contact card & newsletter registration */}
      <roche-grid layout="8-4" key="contact-card">
        <>
          {didSettings?.did_author_contact_card_title && <roche-headline slot="left" level={2} headline={didSettings.did_author_contact_card_title}/>}
          <RocheContactCardPerson
            slot="left"
            blok={{
              contacts: filteredAuthors.current,
              image: blok.author_image,
              description: blok.author_description,
              links: blok.author_links,
            }}
            getComponent={getComponent}
          />
        </>

        {didSettings?.did_newsletter_registration && didSettings.did_newsletter_registration[0]
         && blokToComponent({
           blok: {
             ...didSettings.did_newsletter_registration[0],
             slot: 'right',
           },
           getComponent,
         }, 'newsletter_registration')}
      </roche-grid>

      <roche-grid layout="12" key="related-articles">
        {didSettings?.did_related_articles_title
        && <roche-headline level={2} headline={didSettings.did_related_articles_title}/>}
        {blok.related_articles && blok.related_articles[0] && blokToComponent({
          blok: blok.related_articles[0],
          getComponent,
        }, 'articles')}
      </roche-grid>
    </>
  );
};
